import React, { useState, useEffect } from 'react';
import { usersApi, teamsApi, teamMembersApi } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { Edit2Icon, CheckIcon, CloseIcon } from '../Icons/Icons';
import { getStaticUrl } from '../../api/client';
import './PlayersList.css';

const PlayersList = () => {
  const { user, hasPermission } = useAuth();
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [userTeams, setUserTeams] = useState({});
  const [filters, setFilters] = useState({ search: '', team_id: '' });
  const [editingMMR, setEditingMMR] = useState(null);
  const [savingMMR, setSavingMMR] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [u, t] = await Promise.all([usersApi.getAll(), teamsApi.getAll()]);
      setPlayers(u.data);
      setTeams(t.data);
      const userTeamsMap = {};
      for (const u2 of u.data) {
        try {
          const res = await teamMembersApi.getUserTeams(u2.id);
          userTeamsMap[u2.id] = res.data;
        } catch {
          userTeamsMap[u2.id] = [];
        }
      }
      setUserTeams(userTeamsMap);
    } catch (error) {
      console.error('Erreur chargement:', error);
    }
  };

  const filteredPlayers = players.filter(player => {
    if (player.role === 'admin') return false;
    if (filters.search && !player.username.toLowerCase().includes(filters.search.toLowerCase())) return false;
    if (filters.team_id) {
      const playerTeams = userTeams[player.id] || [];
      if (!playerTeams.some(t => t.team_id === parseInt(filters.team_id))) return false;
    }
    return true;
  });

  const staffRoles = ['ceo', 'coo', 'responsable', 'manager', 'coach'];
  const roleOrder = { ceo: 1, coo: 2, responsable: 3, manager: 4, coach: 5 };
  const staffMembers = [...filteredPlayers.filter(p => staffRoles.includes(p.role))]
    .sort((a, b) => (roleOrder[a.role] || 99) - (roleOrder[b.role] || 99));
  const playerMembers = filteredPlayers.filter(p => p.role === 'player');

  const getRoleName = (role) => ({
    ceo: 'CEO', coo: 'COO', responsable: 'Responsable',
    manager: 'Manager', coach: 'Coach', player: 'Joueur'
  }[role] || role);

  const getRoleColor = (role) => ({
    ceo: 'role-ceo', coo: 'role-coo', responsable: 'role-responsable',
    manager: 'role-manager', coach: 'role-coach', player: 'role-player'
  }[role] || '');

  const getInitials = (username) => username?.charAt(0).toUpperCase() || '?';

  const getUserTeamsDisplay = (userId) => {
    const playerTeams = userTeams[userId] || [];
    if (playerTeams.length === 0) return <span className="pl-no-team">Sans équipe</span>;
    return (
      <div className="pl-team-list">
        {playerTeams.map((t, i) => (
          <span key={i} className="pl-team-badge">{t.team_name}</span>
        ))}
      </div>
    );
  };

  const canEditMMR = (player) => {
    if (!user) return false;
    if (user.id === player.id) return true;
    if (hasPermission('players.edit_mmr_all')) return true;
    if (hasPermission('players.edit_mmr_own_game') && user.responsable_game_ids?.length > 0) {
      const playerTeams = userTeams[player.id] || [];
      return playerTeams.some(teamInfo => {
        const team = teams.find(t => t.id === teamInfo.team_id);
        return team && user.responsable_game_ids.includes(team.game_id);
      });
    }
    if (hasPermission('players.edit_mmr_own_team')) {
      const playerTeams = userTeams[player.id] || [];
      const userTeamIds = user.team_memberships?.map(tm => tm.team_id) || [];
      return playerTeams.some(t => userTeamIds.includes(t.team_id));
    }
    return false;
  };

  const handleEditMMR = (player) => setEditingMMR({ userId: player.id, value: player.mmr || '' });

  const handleSaveMMR = async () => {
    if (!editingMMR || savingMMR) return;
    try {
      setSavingMMR(true);
      const mmrValue = editingMMR.value === '' ? null : parseInt(editingMMR.value);
      await usersApi.update(editingMMR.userId, { mmr: mmrValue });
      setPlayers(players.map(p => p.id === editingMMR.userId ? { ...p, mmr: mmrValue } : p));
      setEditingMMR(null);
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la mise à jour du MMR');
    } finally {
      setSavingMMR(false);
    }
  };

  return (
    <div className="pl-page">
      {/* Header */}
      <header className="pl-header">
        <div>
          <h1 className="pl-title">Membres</h1>
          <p className="pl-subtitle">
            {staffMembers.length} staff · {playerMembers.length} joueur{playerMembers.length > 1 ? 's' : ''}
          </p>
        </div>
      </header>

      {/* Barre de filtres glass */}
      <div className="pl-filter-bar">
        <div className="pl-search-wrapper">
          <svg className="pl-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            type="text"
            className="pl-search-input"
            placeholder="Rechercher un pseudo..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
        </div>
        <div className="pl-filter-pills">
          <button
            className={`pl-pill ${filters.team_id === '' ? 'pl-pill--active' : ''}`}
            onClick={() => setFilters({ ...filters, team_id: '' })}
          >
            Toutes les équipes
          </button>
          {teams.map(team => (
            <button
              key={team.id}
              className={`pl-pill ${filters.team_id === String(team.id) ? 'pl-pill--active' : ''}`}
              onClick={() => setFilters({ ...filters, team_id: filters.team_id === String(team.id) ? '' : String(team.id) })}
            >
              {team.name}
            </button>
          ))}
        </div>
      </div>

      {/* Section Staff */}
      {staffMembers.length > 0 && (
        <section className="pl-section">
          <h2 className="pl-section-title">Staff <span className="pl-count">{staffMembers.length}</span></h2>
          <div className="pl-table-wrapper">
            <table className="pl-table">
              <thead>
                <tr>
                  <th>Pseudo</th>
                  <th>Rôle</th>
                  <th>Équipe(s)</th>
                </tr>
              </thead>
              <tbody>
                {staffMembers.map(player => (
                  <tr key={player.id}>
                    <td>
                      <div className="pl-row-user">
                        <div className="pl-avatar pl-avatar--sm">
                          {player.avatar
                            ? <img src={getStaticUrl(player.avatar)} alt={player.username} />
                            : <span>{getInitials(player.username)}</span>
                          }
                        </div>
                        <span className="pl-username">{player.username}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`pl-role-badge ${getRoleColor(player.role)}`}>
                        {getRoleName(player.role)}
                      </span>
                    </td>
                    <td>{getUserTeamsDisplay(player.id)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Section Joueurs — grille de cards */}
      <section className="pl-section">
        <h2 className="pl-section-title">Joueurs <span className="pl-count">{playerMembers.length}</span></h2>

        {playerMembers.length === 0 ? (
          <p className="pl-empty">Aucun joueur trouvé</p>
        ) : (
          <div className="pl-cards-grid">
            {playerMembers.map(player => (
              <article key={player.id} className="pl-card">
                {/* Hover glow interne */}
                <div className="pl-card-glow" />

                {/* Identité */}
                <div className="pl-card-identity">
                  <div className="pl-avatar pl-avatar--lg">
                    {player.avatar
                      ? <img src={getStaticUrl(player.avatar)} alt={player.username} />
                      : <span>{getInitials(player.username)}</span>
                    }
                  </div>
                  <div>
                    <h3 className="pl-card-name">{player.username}</h3>
                    <p className="pl-card-sub">{getUserTeamsDisplay(player.id)}</p>
                  </div>
                </div>

                {/* Stats */}
                <div className="pl-card-stats">
                  {/* MMR */}
                  <div className="pl-stat">
                    <span className="pl-stat-label">MMR</span>
                    {editingMMR && editingMMR.userId === player.id ? (
                      <div className="pl-mmr-edit">
                        <input
                          type="number"
                          className="pl-mmr-input"
                          value={editingMMR.value}
                          onChange={(e) => setEditingMMR({ ...editingMMR, value: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveMMR();
                            if (e.key === 'Escape') setEditingMMR(null);
                          }}
                          autoFocus
                          disabled={savingMMR}
                        />
                        <button className="pl-mmr-btn pl-mmr-btn--save" onClick={handleSaveMMR} disabled={savingMMR}>
                          <CheckIcon size={14} />
                        </button>
                        <button className="pl-mmr-btn pl-mmr-btn--cancel" onClick={() => setEditingMMR(null)} disabled={savingMMR}>
                          <CloseIcon size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="pl-mmr-display">
                        <span className={player.mmr ? 'pl-stat-value pl-mmr-value' : 'pl-stat-muted'}>
                          {player.mmr || '—'}
                        </span>
                        {canEditMMR(player) && (
                          <button className="pl-btn-edit-mmr" onClick={() => handleEditMMR(player)} title="Modifier le MMR">
                            <Edit2Icon size={13} />
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Profil */}
                  <div className="pl-stat">
                    <span className="pl-stat-label">Profil</span>
                    {player.profile_link ? (
                      <a href={player.profile_link} target="_blank" rel="noopener noreferrer" className="pl-profile-link">
                        Voir →
                      </a>
                    ) : (
                      <span className="pl-stat-muted">—</span>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default PlayersList;
